import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import useSEO from "@/hooks/useSEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  useSEO({ title: "Reset Password", description: "Choose a new password for your CardMyDog account.", path: "/reset-password" });
  const { token } = useParams();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (password !== confirmPassword) { toast.error("Passwords don't match"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen py-16 flex items-center justify-center" data-testid="reset-password-done">
        <div className="max-w-sm w-full mx-auto px-4 text-center">
          <div className="w-14 h-14 rounded-full mx-auto mb-5 flex items-center justify-center" style={{ backgroundColor: "rgba(46,204,113,0.1)" }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
          </div>
          <h1 className="font-heading text-3xl uppercase tracking-tight text-[var(--color-text-dark)] mb-3">Password reset</h1>
          <p className="font-body text-sm text-[var(--color-text-mid)] mb-6">Your password has been updated. You can now sign in.</p>
          <Link to="/login" className="inline-block font-heading tracking-widest uppercase px-8 py-3 rounded-[8px]" style={{ backgroundColor: "var(--color-gold)", color: "var(--color-navy)" }} data-testid="goto-login-btn">Sign In</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-16 flex items-center justify-center" data-testid="reset-password-page">
      <div className="max-w-sm w-full mx-auto px-4">
        <div className="text-center mb-8">
          <h1 className="font-heading text-4xl uppercase tracking-tight text-[var(--color-text-dark)] mb-2">New password</h1>
          <p className="font-body text-sm text-[var(--color-text-mid)]">Choose a new password for your account</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-[var(--color-white)] rounded-[12px] border border-[var(--color-border)] p-6 space-y-4">
          <div>
            <Label className="font-body text-sm text-[var(--color-text-dark)] mb-1.5 block">New password</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 6 characters" className="rounded-[8px]" data-testid="reset-password-input" />
          </div>
          <div>
            <Label className="font-body text-sm text-[var(--color-text-dark)] mb-1.5 block">Confirm password</Label>
            <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Re-enter password" className="rounded-[8px]" data-testid="reset-confirm-input" />
          </div>
          <button type="submit" disabled={loading} className="w-full font-heading tracking-widest uppercase py-3 rounded-[8px] transition-colors disabled:opacity-50" style={{ backgroundColor: "var(--color-gold)", color: "var(--color-navy)" }} data-testid="reset-submit-btn">
            {loading ? "Resetting..." : "Reset Password"}
          </button>
        </form>
      </div>
    </div>
  );
}
